import { px } from '@adsk/alloy-react-helpers';

import { AlloyLegacyThemeDesignTokens, TextEditorDesignTokens } from '../types';
import type { WeaveTheme } from '../types/weaveTypes';

type WeaveTokens = NonNullable<WeaveTheme['tokens']>;

/**
 * Off-scale by design: carried over verbatim from the pre-migration
 * `height: 36` / `minWidth: 36` so the toolbar stays pixel-identical to
 * `master`. Both themes share it because the Figma tool cells are 36x36 too.
 */
const TOOL_SIZE = px(36);

/**
 * Off-scale by design: carried over verbatim from the pre-migration
 * `height: 20` on the divider.
 */
const DIVIDER_HEIGHT = px(20);

export const getTextEditorTokensAlloy = (
  theme: AlloyLegacyThemeDesignTokens,
): TextEditorDesignTokens => {
  const { spacing, borderRadius } = theme;
  const { colors } = theme.tokens;

  return {
    _: {
      background: colors.background.inverse.value,
      'border-radius': px(borderRadius.small),
      'padding-top': px(spacing.XXS),
      'padding-bottom': px(spacing.XXS),
      'padding-left': px(spacing.XXXS),
      'padding-right': px(spacing.XXXS),
    },
    tool: {
      _: {
        'border-radius': px(borderRadius.medium),
        'margin-top': 0,
        'margin-bottom': 0,
        'margin-left': px(spacing.XXXS),
        'margin-right': px(spacing.XXXS),
        'border-width': px(2),
        'border-style': 'solid',
        'border-color': 'transparent',
        height: TOOL_SIZE,
        'min-width': TOOL_SIZE,
      },
      icon: {
        fill: {
          default: colors.icon.inverse.value,
          /**
           * The swatch colour the colour tool falls back to when nothing is
           * selected. Figma specs brand blue here, but Alloy has always used
           * `text.primary`, so parity with `master` wins.
           */
          accent: colors.text.primary.value,
          selected: colors.icon.primary.value,
        },
      },
      /**
       * Alloy never coloured the colour tool's disclosure triangle, so it
       * inherits the button colour. Left undefined to keep that behaviour;
       * the key exists so both factories return the same shape.
       */
      dropdown: {
        _: {
          'padding-left': px(spacing.XXS),
        },
        icon: {
          fill: {
            default: undefined,
            hover: undefined,
            selected: undefined,
          },
        },
      },
      'background-color': {
        default: 'transparent',
        hover: colors.background.inverse.hover.value,
        pressed: colors.background.inverse.pressed.value,
        selected: colors.background.inverse.selected.value,
      },
      'border-color': {
        focus: colors.border.focused.value,
      },
      disabled: {
        color: colors.icon.inactive.value,
        'pointer-events': 'none',
      },
    },
    divider: {
      _: {
        height: DIVIDER_HEIGHT,
        'margin-left': px(spacing.XXS),
        'margin-right': px(spacing.XXS),
      },
      'border-color': colors.border.value,
    },
    content: {
      highlight: {
        'background-color': colors.background.selected.subtle.value,
      },
    },
  };
};

export const getTextEditorTokensWeave = (
  tokens: WeaveTokens | undefined,
): TextEditorDesignTokens => {
  const generic = tokens?.generic;
  const semantic = tokens?.semantic;

  const iconHeavy = semantic?.['icon-color']?.heavy;

  return {
    _: {
      background: semantic?.['background-color']?.brand,
      'border-radius': generic?.['border-radius']?.none,
      'padding-top': generic?.spacing?.[4],
      'padding-bottom': generic?.spacing?.[4],
      'padding-left': generic?.spacing?.[2],
      'padding-right': generic?.spacing?.[2],
    },
    tool: {
      _: {
        'border-radius': generic?.['border-radius']?.[2],
        'margin-top': 0,
        'margin-bottom': 0,
        'margin-left': generic?.spacing?.[2],
        'margin-right': generic?.spacing?.[2],
        'border-width': generic?.['border-width']?.[2],
        'border-style': 'solid',
        'border-color': 'transparent',
        height: TOOL_SIZE,
        'min-width': TOOL_SIZE,
      },
      icon: {
        fill: {
          default: semantic?.['text-color']?.medium?.inverse,
          accent: semantic?.['text-color']?.accent,
          selected: iconHeavy,
        },
      },
      dropdown: {
        _: {
          'padding-left': generic?.spacing?.[4],
        },
        icon: {
          fill: {
            default: semantic?.['icon-color']?.medium,
            hover: semantic?.['icon-color']?.medium,
            selected: iconHeavy,
          },
        },
      },
      'background-color': {
        default: 'transparent',
        hover: semantic?.['background-color']?.neutral?.high?.hover,
        /**
         * Bound to the raw charcoal-500 ramp in Figma rather than a themed
         * token, so it is the same value in every Weave theme.
         */
        pressed: generic?.color?.charcoal?.[500]?.[100],
        selected: semantic?.['background-color']?.overlay?.input?.selected,
      },
      'border-color': {
        focus: semantic?.['border-color']?.accent?.default,
      },
      disabled: {
        color: iconHeavy,
        'pointer-events': 'none',
      },
    },
    divider: {
      _: {
        height: DIVIDER_HEIGHT,
        'margin-left': generic?.spacing?.[4],
        'margin-right': generic?.spacing?.[4],
      },
      'border-color': semantic?.['border-color']?.medium?.inverse,
    },
    content: {
      highlight: {
        'background-color': semantic?.['background-color']?.selected?.default,
      },
    },
  };
};
